import { ref } from 'vue'
import axios from 'axios'
import { sendGraphQLQuery } from '@/services/gql-util'
import { EmailTemplate, NotificationRule, NotificationLog } from '@/types/Notifications'

export function useNotifications() {
	const templates = ref<EmailTemplate[]>([])
	const rules = ref<NotificationRule[]>([])
	const logs = ref<NotificationLog[]>([])
	const loading = ref(false)
	const error = ref<string | null>(null)

	const getEmailTemplates = async (vhost: string): Promise<void> => {
		loading.value = true
		error.value = null

		const query = `
		query ($vhost: Int!) {
			Notifications {
				emailTemplates(vhost: $vhost) {
					id
					name
					vhost
					subject
					body
					type
					trigger
					enabled
					lastModified
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, { vhost: +vhost })
			if (data?.Notifications?.emailTemplates) {
				templates.value = [...data.Notifications.emailTemplates]
			}
		} catch (err: any) {
			console.error('Error fetching email templates:', err)
			error.value = err.message || 'Failed to fetch email templates'
			// Fallback to REST
			try {
				const response = await axios.post('/services/json', {
					method: 'notifications.getEmailTemplates',
					vhostID: vhost,
				})
				if (response.data.status !== false) {
					templates.value = response.data.result?.data || []
				}
			} catch (restErr) {
				console.error('REST fallback also failed:', restErr)
			}
		} finally {
			loading.value = false
		}
	}

	const saveEmailTemplate = async (template: Partial<EmailTemplate>): Promise<boolean> => {
		const mutation = `
		mutation ($id: Int, $vhost: Int!, $name: String!, $subject: String!, $body: String!, $trigger: String, $enabled: Boolean) {
			NotificationMutations {
				saveEmailTemplate(id: $id, vhost: $vhost, name: $name, subject: $subject, body: $body, trigger: $trigger, enabled: $enabled)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, template)
			return true
		} catch (err: any) {
			console.error('Error saving email template:', err)
			return false
		}
	}

	const deleteEmailTemplate = async (templateId: number): Promise<boolean> => {
		const mutation = `
		mutation ($templateId: Int!) {
			NotificationMutations {
				deleteEmailTemplate(templateId: $templateId)
			}
		}
		`

		try {
			await sendGraphQLQuery(mutation, { templateId })
			return true
		} catch (err) {
			console.error('Error deleting email template:', err)
			return false
		}
	}

	const getNotificationRules = async (vhost: string): Promise<void> => {
		loading.value = true

		const query = `
		query ($vhost: Int!) {
			Notifications {
				notificationRules(vhost: $vhost) {
					id
					name
					vhost
					event
					channel
					recipients
					templateId
					enabled
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, { vhost: +vhost })
			if (data?.Notifications?.notificationRules) {
				rules.value = [...data.Notifications.notificationRules]
			}
		} catch (err: any) {
			console.error('Error fetching notification rules:', err)
		} finally {
			loading.value = false
		}
	}

	const getNotificationLogs = async (vhost: string, start: number = 0, limit: number = 50): Promise<void> => {
		loading.value = true

		const query = `
		query ($vhost: Int!, $start: Int, $limit: Int) {
			Notifications {
				notificationLogs(vhost: $vhost, start: $start, limit: $limit) {
					id
					templateId
					templateName
					recipient
					subject
					status
					sentDate
					errorMessage
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, {
				vhost: +vhost,
				start,
				limit,
			})
			if (data?.Notifications?.notificationLogs) {
				logs.value = [...data.Notifications.notificationLogs]
			}
		} catch (err: any) {
			console.error('Error fetching notification logs:', err)
		} finally {
			loading.value = false
		}
	}

	return {
		templates,
		rules,
		logs,
		loading,
		error,
		getEmailTemplates,
		saveEmailTemplate,
		deleteEmailTemplate,
		getNotificationRules,
		getNotificationLogs,
	}
}
